import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Checkbox,
  Alert,
  CircularProgress,
  ListItemText,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import api from '../../services/api';
import { OrderCategory, OrderSubcategory } from '../../types/order';

function AddOnsPage() {
  const [addons, setAddons] = useState<any[]>([]);
  const [categories, setCategories] = useState<OrderCategory[]>([]);
  const [allSubcategories, setAllSubcategories] = useState<OrderSubcategory[]>([]);
  const [subcategories, setSubcategories] = useState<OrderSubcategory[]>([]);
  const [filterCategoryId, setFilterCategoryId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAddon, setEditingAddon] = useState<any | null>(null);

  const [formData, setFormData] = useState({
    category_id: '',
    name: '',
    description: '',
    sku: '',
    monthly_price: '',
    setup_price: '0',
    one_time_price: '0',
    subcategory_ids: [] as string[],
    display_order: 0,
    is_active: true,
    service_type_code: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (formData.category_id) {
      loadSubcategories(formData.category_id);
    } else {
      setSubcategories([]);
      setFormData((p) => ({ ...p, subcategory_ids: [] }));
    }
  }, [formData.category_id]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [addonData, catData, subcatData] = await Promise.all([
        api.adminListAddons(),
        api.adminListCategories(),
        api.adminListSubcategories(),
      ]);
      setAddons(addonData);
      setCategories(catData);
      setAllSubcategories(subcatData);
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadSubcategories = async (categoryId: string) => {
    try {
      const subcats = await api.adminListSubcategories(categoryId);
      setSubcategories(subcats);
    } catch (err) {
      setSubcategories([]);
    }
  };

  const handleOpenDialog = (item?: any) => {
    if (item) {
      setEditingAddon(item);
      setFormData({
        category_id: item.category_id || '',
        name: item.name,
        description: item.description || '',
        sku: item.sku || '',
        monthly_price: String(item.monthly_price ?? ''),
        setup_price: String(item.setup_price ?? '0'),
        one_time_price: String(item.one_time_price ?? '0'),
        subcategory_ids: item.subcategory_ids || [],
        display_order: item.display_order ?? 0,
        is_active: item.is_active ?? true,
        service_type_code: item.service_type_code || '',
      });
      if (item.category_id) loadSubcategories(item.category_id);
    } else {
      setEditingAddon(null);
      setFormData({
        category_id: '',
        name: '',
        description: '',
        sku: '',
        monthly_price: '',
        setup_price: '0',
        one_time_price: '0',
        subcategory_ids: [],
        display_order: 0,
        is_active: true,
        service_type_code: '',
      });
      setSubcategories([]);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingAddon(null);
  };

  const handleSave = async () => {
    try {
      setError(null);
      const payload: any = {
        category_id: formData.category_id,
        name: formData.name,
        description: formData.description || undefined,
        sku: formData.sku || undefined,
        monthly_price: parseFloat(formData.monthly_price) || 0,
        setup_price: parseFloat(formData.setup_price) || 0,
        one_time_price: parseFloat(formData.one_time_price) || 0,
        subcategory_ids: formData.subcategory_ids,
        display_order: formData.display_order,
        is_active: formData.is_active,
        service_type_code: formData.service_type_code || null,
      };
      if (editingAddon) {
        await api.adminUpdateAddon(editingAddon.id, payload);
      } else {
        await api.adminCreateAddon(payload);
      }
      handleCloseDialog();
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to save');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this add-on?')) return;
    try {
      await api.adminDeleteAddon(id);
      loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete');
    }
  };

  const formatPrice = (v?: number) => (v != null ? `$${Number(v).toFixed(2)}` : '—');
  const getCategoryName = (cid?: string) => categories.find((c) => c.id === cid)?.name ?? '—';
  const getSubcategoryNames = (ids: string[]) =>
    ids.map((id) => allSubcategories.find((s) => s.id === id)?.name).filter(Boolean).join(', ') || '—';

  const filtered = filterCategoryId ? addons.filter((a) => a.category_id === filterCategoryId) : addons;

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Name', width: 200 },
    { field: 'sku', headerName: 'SKU', width: 140 },
    { field: 'category_name', headerName: 'Category', width: 140, valueGetter: (p: any) => getCategoryName(p.row?.category_id) },
    {
      field: 'subcategories',
      headerName: 'Assigned to',
      width: 220,
      valueGetter: (p: any) => getSubcategoryNames(p.row?.subcategory_ids || []),
    },
    { field: 'monthly_price', headerName: 'Monthly', width: 100, renderCell: (p) => formatPrice(p.value) },
    { field: 'setup_price', headerName: 'Setup', width: 90, renderCell: (p) => formatPrice(p.value) },
    { field: 'one_time_price', headerName: 'One-time', width: 100, renderCell: (p) => formatPrice(p.value) },
    { field: 'display_order', headerName: 'Order', width: 80 },
    { field: 'is_active', headerName: 'Active', width: 80, type: 'boolean' },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (p: any) => (
        <Box>
          <IconButton size="small" onClick={() => handleOpenDialog(p.row)}>
            <EditIcon />
          </IconButton>
          <IconButton size="small" onClick={() => handleDelete(p.row.id)} color="error">
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">Add-ons</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Add Add-on
          </Button>
        </Box>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Box sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Category</InputLabel>
            <Select
              value={filterCategoryId}
              label="Filter by Category"
              onChange={(e) => setFilterCategoryId(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={filtered}
            columns={columns}
            paginationModel={{ page: 0, pageSize: 25 }}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            autoHeight
            getRowId={(r) => r.id}
          />
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingAddon ? 'Edit Add-on' : 'Add Add-on'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category_id}
                  label="Category"
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                >
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SKU"
                value={formData.sku}
                onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                label="Monthly Price ($)"
                value={formData.monthly_price}
                onChange={(e) => setFormData({ ...formData, monthly_price: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                label="Setup Price ($)"
                value={formData.setup_price}
                onChange={(e) => setFormData({ ...formData, setup_price: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                label="One-time Price ($)"
                value={formData.one_time_price}
                onChange={(e) => setFormData({ ...formData, one_time_price: e.target.value })}
                helperText="Use for a pure one-off charge instead of monthly"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Assigned to (Subcategories)</InputLabel>
                <Select
                  multiple
                  value={formData.subcategory_ids}
                  label="Assigned to (Subcategories)"
                  onChange={(e) => setFormData({ ...formData, subcategory_ids: e.target.value as string[] })}
                  renderValue={(sel) =>
                    (sel as string[])
                      .map((id) => subcategories.find((s) => s.id === id)?.name)
                      .filter(Boolean)
                      .join(', ')
                  }
                >
                  {subcategories.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      <Checkbox checked={formData.subcategory_ids.includes(s.id)} />
                      <ListItemText primary={s.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary">
                Tick every network this add-on should be offered on (e.g. NBN and Supa) — new networks can be
                ticked here later without any code changes.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Provisioning"
                value={formData.service_type_code}
                onChange={(e) => setFormData({ ...formData, service_type_code: e.target.value })}
                helperText="Add-ons with provisioning get an action on the order and become a service on the customer's account."
              >
                <MenuItem value="">None (billing line only)</MenuItem>
                <MenuItem value="static_ip">Static IP Address (assign the IP manually)</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Display Order"
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.name || !formData.category_id || !formData.monthly_price}
          >
            {editingAddon ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AddOnsPage;
